const dedupeWindowMs = 1500;
const recentEventFingerprints = new Map();
const allowedEventParamKeys = new Set([
  'page_location',
  'page_title',
  'form_id',
  'form_name',
  'form_destination',
  'form_location',
  'lead_type',
  'cta_text',
  'cta_location',
  'cta_destination',
  'service_name',
  'service_category',
  'link_url',
  'link_domain',
  'video_title',
  'video_url',
  'currency',
  'value',
  'transaction_id',
  'items',
  'event_category',
  'campaign_id',
  'recipient_count',
  'purchased_blocks',
  'balance_remaining',
  'checkout_step',
  'esp',
  'personalization_type',
]);

let hasInitializedAnalytics = false;

const canUseWindow = typeof window !== 'undefined';

const sanitizeEventParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([key, value]) => {
      if (!allowedEventParamKeys.has(key) || value === undefined || value === null || value === '') {
        return false;
      }

      if (key === 'items' && !Array.isArray(value)) {
        return false;
      }

      return true;
    }),
  );

const stableSerialize = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${key}:${stableSerialize(value[key])}`)
      .join(',')}}`;
  }

  return String(value);
};

const pruneRecentEventFingerprints = (now) => {
  for (const [fingerprint, timestamp] of recentEventFingerprints.entries()) {
    if (now - timestamp > dedupeWindowMs) {
      recentEventFingerprints.delete(fingerprint);
    }
  }
};

const shouldSkipDuplicateEvent = (fingerprint) => {
  const now = Date.now();
  const lastSeenAt = recentEventFingerprints.get(fingerprint);

  pruneRecentEventFingerprints(now);

  if (lastSeenAt && now - lastSeenAt < dedupeWindowMs) {
    return true;
  }

  recentEventFingerprints.set(fingerprint, now);
  return false;
};

export const initializeAnalytics = () => {
  if (hasInitializedAnalytics || !canUseWindow) {
    return false;
  }

  window.dataLayer = window.dataLayer || [];
  hasInitializedAnalytics = true;
  return true;
};

export const trackGa4Event = (eventName, params = {}, options = {}) => {
  if (!canUseWindow || !eventName) {
    return false;
  }

  if (!hasInitializedAnalytics) {
    initializeAnalytics();
  }

  const sanitizedParams = sanitizeEventParams(params);
  const dedupeKey = options.dedupeKey || stableSerialize(sanitizedParams);
  const fingerprint = `${eventName}|${dedupeKey}`;

  if (shouldSkipDuplicateEvent(fingerprint)) {
    return false;
  }

  try {
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: eventName,
        ...sanitizedParams,
      });
      return true;
    }
  } catch {
    return false;
  }

  return false;
};

export const trackFormStart = (params) =>
  trackGa4Event('form_start', params, {
    dedupeKey: `${params.form_id || 'form'}|${params.page_location || ''}`,
  });

export const trackFormSubmit = (params) =>
  trackGa4Event('form_submit', params, {
    dedupeKey: `${params.form_id || 'form'}|${params.page_location || ''}`,
  });

export const trackGenerateLead = (params) =>
  trackGa4Event('generate_lead', params, {
    dedupeKey: `${params.form_id || 'form'}|${params.lead_type || 'lead'}|${params.page_location || ''}`,
  });

export const trackCtaClick = (params) =>
  trackGa4Event('cta_clicked', params, {
    dedupeKey: `${params.cta_text || 'cta'}|${params.cta_location || ''}|${params.cta_destination || ''}|${params.page_location || ''}`,
  });

export const trackServiceInterestSelected = (params) =>
  trackGa4Event('service_interest_selected', params, {
    dedupeKey: `${params.service_name || 'service'}|${params.service_category || ''}|${params.page_location || ''}`,
  });
